import { blue, neutral } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { SxProps, Theme } from "@mui/material";
import { BulkActionsMode } from "@hooks/use-bulk-actions-mode";

/**
 * Configuration object for bulk action button styling and behavior
 */
interface BulkActionButtonConfig {
  /** Tooltip text to display on hover */
  tooltipText: string | undefined;
  /** Color for the loading track animation */
  loadingTrackColor: string;
  /** Color for the button ripple effect */
  rippleColor: string;
  /** Whether to show text label in the button */
  shouldShowText: boolean;
  /** MUI sx prop for custom styling */
  sx: SxProps<Theme>;
}

/**
 * Returns button configuration for each bulk action mode.
 * Provides consistent styling across all bulk action buttons.
 *
 * @param mode - The current bulk actions display mode
 * @param tooltipText - The tooltip text to show (only in non-collapsed modes)
 * @returns Configuration object with styling and behavior settings
 */
export function getBulkActionButtonConfig(
  mode: BulkActionsMode,
  tooltipText: string
): BulkActionButtonConfig {
  const configs: Record<BulkActionsMode, BulkActionButtonConfig> = {
    collapsed: {
      tooltipText: undefined,
      loadingTrackColor: neutral[400],
      rippleColor: neutral[500],
      shouldShowText: true,
      sx: {
        width: "100%",
        justifyContent: "flex-start",
        color: neutral[800],
        "&:hover": {
          color: blue[500],
        },
        "& span": {
          color: "inherit",
        },
      },
    },
    compact: {
      tooltipText,
      loadingTrackColor: neutral[200],
      rippleColor: neutral[500],
      shouldShowText: false,
      sx: {
        color: neutral[0],
        minWidth: "auto",
        "& span": {
          margin: 0,
        },
      },
    },
    full: {
      tooltipText,
      loadingTrackColor: neutral[200],
      rippleColor: neutral[500],
      shouldShowText: true,
      sx: {
        color: neutral[0],
        maxWidth: 200,
        "& > span:last-child": {
          color: neutral[0],
          ...withEllipsis,
        },
      },
    },
  };

  return configs[mode];
}
